import React, { useState, useEffect, useContext } from 'react';
import { Plus, X, Save, ArrowLeft, Calendar, AlertTriangle, User } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import riskService from '../../../../services/riskService';

import { useToast } from '../../../../context/ToastContext';
import userService from '../../../../services/userService';

export default function EditRisk() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    probabilite: '',
    riskDate: '',
    consequence: ''
  });

  const [actions, setActions] = useState([{
    id: null,
    description: '',
    assignedUser: null,
    assignedUserDisplay: '',
    selectedRole: null,
    dueDate: ''
  }]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [risk, setRisk] = useState(null);

  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [currentActionIndex, setCurrentActionIndex] = useState(null);

  const probabiliteOptions = [
    { value: 'mineur', label: 'Mineur', color: 'bg-blue-100 text-blue-800' },
    { value: 'normal', label: 'Normal', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'majeur', label: 'Majeur', color: 'bg-orange-100 text-orange-800' },
    { value: 'critique', label: 'Critique', color: 'bg-red-100 text-red-800' }
  ];

  // Fetch risk data when component mounts
  useEffect(() => {
    const fetchRisk = async () => {
      if (!id) {
        navigate('/dashboard/risks');
        return;
      }

      setLoading(true);
      try {
        const result = await riskService.getRiskDetails(id, authTokens.access);
        
        if (result.success) {
          setRisk(result.data);
        } else {
          showError(result.error || 'Erreur lors du chargement du risque');
          navigate('/dashboard/risks');
        }
      } catch (error) {
        console.error('Error fetching risk:', error);
        showError('Erreur de connexion');
        navigate('/dashboard/risks');
      } finally {
        setLoading(false);
      }
    };

    fetchRisk();
  }, [id, authTokens.access, navigate]);

  // Pre-populate form with risk data when risk is loaded
  useEffect(() => {
    if (risk) {
      setFormData({
        nom: risk.risk_name || '',
        description: risk.description || '',
        probabilite: risk.probability?.toLowerCase() || '',
        riskDate: risk.risk_date || '',
        consequence: risk.consequence || ''
      });
      
      if (risk.actions && risk.actions.length > 0) {
        const processedActions = risk.actions.map(action => ({
          id: action.id,
          description: action.action || '',
          assignedUser: action.assignedUserObject || null,
          assignedUserDisplay: action.assignedUserObject 
            ? `${action.assignedUserObject.first_name} ${action.assignedUserObject.last_name} (${action.assignedUserObject.email})`
            : '',
          selectedRole: action.userRoleAssignedObject || null,  // ✅ This now matches the serializer
          dueDate: action.dueDate || ''
        }));
        setActions(processedActions);
      } else {
        setActions([{ 
          id: null,
          description: '', 
          assignedUser: null, 
          assignedUserDisplay: '', 
          selectedRole: null,
          dueDate: '' 
        }]);
      }
    }
  }, [risk]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleActionChange = (index, field, value) => {
    const newActions = [...actions];
    newActions[index] = { ...newActions[index], [field]: value };
    setActions(newActions);
  };

  const addAction = () => {
    setActions([...actions, { 
      id: null,
      description: '', 
      assignedUser: null,
      assignedUserDisplay: '',
      selectedRole: null,
      dueDate: '' 
    }]);
  };

  const removeAction = (index) => {
    if (actions.length > 1) {
      setActions(actions.filter((_, i) => i !== index));
    }
  };

  const searchUsers = async (query, actionIndex) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    try {
      const result = await userService.filterUsersBySearch(query, authTokens.access);
      if (result.success) {
        setSearchResults(result.data);
        setShowUserDropdown(true);
        setCurrentActionIndex(actionIndex);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
      setShowUserDropdown(false);
    }
  };

  const handleUserSearchInput = (query, actionIndex) => {
    const newActions = [...actions];
    newActions[actionIndex] = { 
      ...newActions[actionIndex], 
      assignedUserDisplay: query,
      assignedUser: null,
      selectedRole: null
    };
    setActions(newActions);
    
    setUserSearchQuery(query);
    searchUsers(query, actionIndex);
  };

  const selectUser = (selectedUser, actionIndex) => {
    const userDisplay = `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})`;
    
    const newActions = [...actions];
    newActions[actionIndex] = {
      ...newActions[actionIndex],
      assignedUser: selectedUser,
      assignedUserDisplay: userDisplay,
      selectedRole: null
    };
    setActions(newActions);
    
    setUserSearchQuery('');
    setShowUserDropdown(false);
    setSearchResults([]);
    setCurrentActionIndex(null);
  };

  const handleRoleSelect = (actionIndex, role) => {
    const newActions = [...actions];
    newActions[actionIndex] = {
      ...newActions[actionIndex],
      selectedRole: role
    };
    setActions(newActions);
  };

  const clearUserSelection = (actionIndex) => {
    const newActions = [...actions];
    newActions[actionIndex] = {
      ...newActions[actionIndex],
      assignedUser: null,
      assignedUserDisplay: '',
      selectedRole: null
    };
    setActions(newActions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate actions with users must have roles selected
    const invalidActions = actions.filter(action => 
      action.description.trim() !== '' && 
      !action.assignedUser 
      // !action.selectedRole
    );

    if (invalidActions.length > 0) {
      showError("Veuillez sélectionner un rôle pour tous les utilisateurs assignés.");
      setIsSubmitting(false);
      return;
    }

    // Only include actions with descriptions
    const validActions = actions.filter(action => action.description.trim() !== '');

    const processedActions = validActions.map(action => ({
      id: action.id || undefined,  // ✅ Include id for existing actions
      action: action.description,
      assignedUserId: action.assignedUser ? action.assignedUser.id : null,
      userRoleAssignedId: action.selectedRole ? action.selectedRole.id : null,
      dueDate: action.dueDate || null,
    }));

    // Match backend field names exactly
    const backendData = {
      risk_name: formData.nom,
      description: formData.description,
      probability: formData.probabilite,
      risk_date: formData.riskDate,
      consequence: formData.consequence,
      actions: processedActions,
    };

    console.log('Updating risk with data:', backendData);

    try {
      const result = await riskService.updateRisk(id, backendData, authTokens.access);

      if (result.success) {
        showSuccess("Risque mis à jour avec succès !");
        setTimeout(() => {
          navigate('/dashboard/risks');
        }, 1500);
      } else {
        console.error('Backend error:', result.errorData);
        showError(result.error || "Erreur lors de la mise à jour du risque");
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      showError("Erreur inattendue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/risks');
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // No risk found
  if (!risk && !loading) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">
            Risque introuvable
          </h3>
          <p className="text-yellow-600 mb-4">
            Le risque demandé n'a pas pu être trouvé.
          </p>
          <button
            onClick={() => navigate('/dashboard/risks')}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
          >
            Retour à la liste des risques
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <button
          onClick={handleCancel}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Modifier le risque</h2>
          <p className="text-sm text-gray-500">ID: {risk?.id}</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white border rounded-lg shadow-sm p-6 space-y-6">
        
        {/* Risk Name and Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du risque <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              placeholder="Entrer le nom du risque..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Date du risque <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.riskDate}
              onChange={(e) => handleInputChange('riskDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
              required
            />
          </div>
        </div>

        {/* Consequence */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Conséquence <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.consequence}
            onChange={(e) => handleInputChange('consequence', e.target.value)}
            placeholder="Décrire la conséquence du risque..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Décrire le risque en détail..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
            required
          />
        </div>

        {/* Probability */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Probabilité <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {probabiliteOptions.map(option => (
              <label
                key={option.value}
                className={`flex items-center justify-center p-3 border rounded-lg cursor-pointer ${
                  formData.probabilite === option.value
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="probabilite"
                  value={option.value}
                  checked={formData.probabilite === option.value}
                  onChange={(e) => handleInputChange('probabilite', e.target.value)}
                  className="sr-only"
                />
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${option.color}`}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">
              Actions préventives/correctives
            </label>
            <button
              type="button"
              onClick={addAction}
              className="flex items-center px-3 py-1 text-sm bg-teal-100 text-teal-700 rounded-md hover:bg-teal-200"
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajouter une action
            </button>
          </div>

          <div className="space-y-4">
            {actions.map((action, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Action {index + 1}</h4>
                  {actions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAction(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded-md"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {/* Action Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Description de l'action
                    </label>
                    <textarea
                      value={action.description}
                      onChange={(e) => handleActionChange(index, 'description', e.target.value)}
                      placeholder={`Décrire l'action ${index + 1}...`}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* User Assignment */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <User className="w-3 h-3 inline mr-1" />
                        Utilisateur assigné
                      </label>
                      <input
                        type="text"
                        value={action.assignedUserDisplay || ''}
                        onChange={(e) => handleUserSearchInput(e.target.value, index)}
                        placeholder="Rechercher un utilisateur..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                      />

                      {/* User Search Dropdown */}
                      {showUserDropdown && currentActionIndex === index && searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto">
                          {searchResults.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => selectUser(user, index)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm"
                            >
                              <div className="font-medium">
                                {user.first_name} {user.last_name} <span className="text-gray-500 text-xs">{user.role_function}</span>
                              </div>
                              <div className="text-gray-500 text-xs">
                                {user.email}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Due Date */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Date d'échéance
                      </label>
                      <input
                        type="date"
                        value={action.dueDate}
                        onChange={(e) => handleActionChange(index, 'dueDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* Role Selection - Shows after user is selected */}
                  {action.assignedUser && (
                    <div className="mt-3 p-3 bg-white border border-gray-200 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-gray-700">
                          {/* Sélectionner le rôle */}
                          Rôle/position de l'utilisateur sélectionné: {action.assignedUser.role_function}
                        </label>
                        {/* <button
                          type="button"
                          onClick={() => clearUserSelection(index)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Changer d'utilisateur
                        </button> */}
                      </div>

                      {/* {action.assignedUser.roles && action.assignedUser.roles.length === 0 ? (
                        <div className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-md">
                          ⚠️ Cet utilisateur n'a aucun rôle assigné. Veuillez sélectionner un autre utilisateur.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {action.assignedUser.roles.map((role) => (
                            <label
                              key={role.id}
                              className={`flex items-center p-2 border rounded-md cursor-pointer text-sm ${
                                action.selectedRole?.id === role.id
                                  ? 'border-teal-500 bg-teal-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`role-${index}`}
                                checked={action.selectedRole?.id === role.id}
                                onChange={() => handleRoleSelect(index, role)}
                                className="mr-2"
                              />
                              <span className="font-medium">{role.role_name}</span>
                            </label>
                          ))}
                        </div>
                      )} */}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Mise à jour...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Mettre à jour le risque
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}